import { defineElement, html, TemplateElement } from '@webtides/element-js';
import { stringify } from 'qs';

const convertStringToHTML = (template) => {
    const parser = new DOMParser();
    const document = parser.parseFromString(template.toString(), 'text/html');
    return document.body;
};

/**
 * @property {string} pageId
 * @property {string} contentPath
 * @property {number} pageSize
 * @property {number} currentPage
 * @property {array} pageIds
 */
export default class PagesVisitedListComponent extends TemplateElement {
    constructor() {
        super({ shadowRender: true });
    }

    properties() {
        return {
            pageId: '',
            contentPath: '',
            pageSize: 7,
            currentPage: 0,
            pageIds: [],
        };
    }

    connected() {
        if (this.pageIds.length > 0) return;
        const visitedPages = JSON.parse(window.localStorage.getItem('visitedPages')) || [];
        this.pageIds = visitedPages.reverse();
    }

    afterUpdate() {
        const nextPageContainer = this.querySelector('.next-page');
        const nextPageButton = this.querySelector('.next-page .btn');
        nextPageContainer.classList.toggle('d-none', !this.hasNextPage);

        nextPageButton.textContent =
            this.remainingPages > 1
                ? `${this.pageSize} ${nextPageButton.getAttribute('button-text-next')}`
                : nextPageButton.getAttribute('button-text-remaining');
    }

    watch() {
        return {
            pageIds: (pageIds) => {
                if (this.pageIds.length > 0) {
                    /** @type ShareButton */
                    const visitedListShareButton = this.querySelector('[data-share-visited-list]');
                    if (visitedListShareButton) {
                        const currentUrl = window.location.href.split('#')[0];
                        visitedListShareButton.url = `${currentUrl}?pages=${this.pageIds.join(',')}${window.location.hash}`;
                    }

                    this.fetchPages();
                }
            },
        };
    }

    events() {
        return {
            this: {
                click: (e) => {
                    if (e.target.closest('[data-clear-visited-list]')) {
                        this.clearWatched();
                        return;
                    }

                    if (e.target.closest('[remove-from-list]')) {
                        const pageId = e.target.closest('[remove-from-list]').getAttribute('remove-from-list');
                        if (pageId) {
                            this.removeFromVisited(pageId);
                        }
                    }

                    if (e.target.closest('[load-next-page]')) {
                        e.preventDefault();
                        this.currentPage++;
                        this.appendPages();
                    }
                },
            },
        };
    }

    get hasNextPage() {
        const totalItems = this.pageIds.length;
        const totalPages = totalItems / this.pageSize;

        return this.currentPage < totalPages - 1;
    }

    get remainingPages() {
        const totalItems = this.pageIds.length;
        const totalPages = totalItems / this.pageSize;
        return totalPages - this.currentPage - 1;
    }

    get filteredPageIds() {
        const startItem = this.currentPage * this.pageSize;
        const endItem = startItem + this.pageSize;

        return this.pageIds.slice(startItem, endItem);
    }

    async fetchPage(page, showLoadingSpinner = true) {
        const filter = { pages: this.filteredPageIds, currentPage: page };
        const query = stringify(filter, { arrayFormat: 'comma' });

        if (showLoadingSpinner) this.dispatch('loading-start');
        const response = await fetch(`${this.contentPath}?${query}`);
        const htmlString = await response.text();
        if (showLoadingSpinner) this.dispatch('loading-end');
        return convertStringToHTML(htmlString);
    }

    fetchPages() {
        this.fetchPage(this.currentPage).then((html) => {
            const pages = html.querySelector('.pages');
            const container = this.querySelector('.pages');
            container.replaceChildren(...pages.childNodes);
        });
    }

    appendPages() {
        this.fetchPage(this.currentPage, false).then((html) => {
            const pages = html.querySelector('.pages');
            const container = this.querySelector('.pages');
            container.append(...pages.childNodes);
        });
    }

    clearWatched() {
        const pageIds = [];
        window.localStorage.setItem('visitedPages', JSON.stringify(pageIds));
        this.pageIds = pageIds;
    }

    removeFromVisited(pageId) {
        const visitedPages = new Set(JSON.parse(window.localStorage.getItem('visitedPages')));
        visitedPages.delete(pageId);
        const pageIds = Array.from(visitedPages);
        window.localStorage.setItem('visitedPages', JSON.stringify(pageIds));
        this.pageIds = pageIds;
    }

    template() {
        if (this.filteredPageIds.length > 0) {
            return html` <slot></slot> `;
        }
        return html` <slot name="fallback"></slot> `;
    }
}

defineElement('pages-visited-list-component', PagesVisitedListComponent);
